import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, inject, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { ModalComponent } from '../../basic/modal/modal.component';
import { ToastService } from '@shared/service/toast.service';
import { ReviewFormService, type ReviewDraft } from '@shared/service/review-form.service';
import {
  emptyReviewFormModel, type ReviewFormModel, type ReviewLine,
} from '@shared/utils/review-form.util';

/**
 * "Rate your experience" (mockup `openReviewForm` / `reviewFormHtml`) — a popup, wherever it is
 * opened from.
 *
 * The customer reaches it two ways, and both must land on the same form: the booking detail's "How
 * was your event?" card, and the "Leave a review" chip on the bookings LIST. The chip used to
 * navigate to the booking detail with `?review=1`, which read as the page refreshing under them —
 * the mockup's `data-revopen` never leaves the list, it just opens the modal over it.
 *
 * The draft is held here and seeded from `model` on open, so an abandoned form leaves nothing
 * behind and re-opening EDITS the review already left rather than stacking a second one.
 */
@Component({
  selector: 'app-review-form',
  standalone: true,
  styleUrl: './review-form.component.scss',
  imports: [CommonModule, ModalComponent],
  templateUrl: './review-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewFormComponent {
  private readonly reviews = inject(ReviewFormService);
  private readonly toast = inject(ToastService);

  /** The booking being rated, and who is rating it (Review rows are written against both). */
  @Input() bookingId = 0;
  @Input() customerId = 0;
  @Input() userId = 0;

  /** Seeds the form. Re-assigning it (opening the popup for another booking) resets the draft. */
  @Input()
  get model(): ReviewFormModel { return this.modelSig(); }
  set model(m: ReviewFormModel | null) {
    this.modelSig.set(m ?? emptyReviewFormModel());
    this.seed();
  }

  @Input()
  get open(): boolean { return this.openSig(); }
  set open(v: boolean) { this.openSig.set(v); }

  @Output() readonly close = new EventEmitter<void>();
  /** A review was written. The host reloads — the card, the chip and the rating all move with it. */
  @Output() readonly saved = new EventEmitter<void>();

  protected readonly modelSig = signal<ReviewFormModel>(emptyReviewFormModel());
  protected readonly openSig = signal(false);
  protected readonly saving = signal(false);
  protected readonly overall = signal<{ stars: number; text: string }>({ stars: 0, text: '' });
  protected readonly services = signal<Record<number, { stars: number; note: string }>>({});
  protected readonly stars = [1, 2, 3, 4, 5];

  /** The overall rating is the one thing a review can't go in without (mockup submitReview). */
  protected readonly valid = computed(() => this.overall().stars > 0);
  /** An existing review means this is an edit — it changes both the title and the submit label. */
  protected readonly editing = computed(() => this.modelSig().reviewed);

  protected starsOf(line: ReviewLine): number {
    return line.offeringId == null
      ? this.overall().stars
      : (this.services()[line.offeringId]?.stars ?? 0);
  }

  protected noteOf(line: ReviewLine): string {
    return line.offeringId == null
      ? this.overall().text
      : (this.services()[line.offeringId]?.note ?? '');
  }

  protected setStars(line: ReviewLine, n: number): void {
    if (line.offeringId == null) { this.overall.update(o => ({ ...o, stars: n })); return; }
    const id = line.offeringId;
    this.services.update(p => ({ ...p, [id]: { stars: n, note: p[id]?.note ?? '' } }));
  }

  protected setNote(line: ReviewLine, text: string): void {
    if (line.offeringId == null) { this.overall.update(o => ({ ...o, text })); return; }
    const id = line.offeringId;
    this.services.update(p => ({ ...p, [id]: { stars: p[id]?.stars ?? 0, note: text } }));
  }

  protected onClose(): void {
    this.openSig.set(false);
    this.close.emit();
  }

  protected async submit(): Promise<void> {
    if (!this.valid() || this.saving()) return;
    const model = this.modelSig();
    const editing = this.editing();
    this.saving.set(true);
    try {
      const draft: ReviewDraft = { overall: this.overall(), services: this.services() };
      await this.reviews.save({
        model, draft, bookingId: this.bookingId, customerId: this.customerId, userId: this.userId,
      });
      // Close BEFORE telling the host: it reloads, and a host that gates its body on a loading flag
      // would otherwise re-render the modal onto a template that has already been torn down.
      this.openSig.set(false);
      this.toast.showSuccess(editing
        ? 'Your review has been updated'
        : `Thanks — your review is live on ${model.vendorName}'s page`);
      this.saved.emit();
    } catch (err) {
      console.error('review save failed', err);
      this.toast.showError('Could not save your review — please try again');
    } finally {
      this.saving.set(false);
    }
  }

  /** Copy whatever the customer already said into the editable draft. */
  private seed(): void {
    const m = this.modelSig();
    this.overall.set({ stars: m.overall.rating, text: m.overall.text });
    const svc: Record<number, { stars: number; note: string }> = {};
    for (const s of m.services) svc[s.offeringId ?? 0] = { stars: s.rating, note: s.text };
    this.services.set(svc);
  }
}
